import { detectPlatform, type Platform } from './platform.js';
import {
  getMicrosoftStoreDetailUrl,
  getMicrosoftStoreReviewUrl,
  normalizeMicrosoftStoreId,
} from './storeId.js';

/**
 * A "rate us" action describing how a platform should react when the user
 * asks to rate the app.
 */
export type RateAction =
  /**
   * Launch the native Microsoft Store review flow (with web fallback if the
   * protocol handler never takes focus). Only meaningful on Windows; on other
   * platforms it falls back to opening the store detail page in the browser.
   */
  | { type: 'microsoft-store'; storeId: string; fallbackDelayMs?: number }
  /**
   * Open an App Store product page on iOS.
   */
  | { type: 'app-store'; appId: string }
  /**
   * Open a Google Play product page on Android.
   */
  | { type: 'play-store'; packageName: string }
  /**
   * Navigate to any URL.
   */
  | { type: 'url'; url: string; target?: '_self' | '_blank' }
  /**
   * Run custom user code.
   */
  | { type: 'custom'; run: () => void };

/**
 * A "feedback" action describing how a platform should react when the user
 * wants to send feedback.
 */
export type FeedbackAction =
  | { type: 'url'; url: string; target?: '_self' | '_blank' }
  | { type: 'mailto'; to: string; subject?: string; body?: string }
  | { type: 'custom'; run: () => void };

export interface PlatformActions {
  rate?: RateAction;
  feedback?: FeedbackAction;
}

/**
 * Per-platform action configuration for a rate-us surface.
 *
 * `defaults` is used for any platform that has no override. A specific
 * `overrides[platform]` entry is merged on top of `defaults` (per action).
 */
export interface RateUsConfig {
  defaults?: PlatformActions;
  overrides?: Partial<Record<Platform, PlatformActions>>;
}

/**
 * Resolve the actions that apply to the given platform, merging defaults with
 * any platform-specific overrides.
 */
export function resolveActions(
  config: RateUsConfig,
  platform: Platform = detectPlatform(),
): PlatformActions {
  const defaults = config.defaults ?? {};
  const override = config.overrides?.[platform] ?? {};
  return {
    rate: override.rate ?? defaults.rate,
    feedback: override.feedback ?? defaults.feedback,
  };
}

function openUrl(url: string, target: '_self' | '_blank' = '_self'): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (target === '_blank') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.assign(url);
  }
}

/**
 * Execute a `RateAction`. Returns `true` if the action was dispatched, `false`
 * if the action was invalid or could not run (e.g. no `window`).
 */
export function runRateAction(action: RateAction): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  switch (action.type) {
    case 'microsoft-store': {
      const storeId = normalizeMicrosoftStoreId(action.storeId);
      if (!storeId) {
        return false;
      }
      const detailUrl = getMicrosoftStoreDetailUrl(storeId);

      if (detectPlatform() !== 'windows') {
        window.open(detailUrl, '_blank', 'noopener,noreferrer');
        return true;
      }

      const reviewUrl = getMicrosoftStoreReviewUrl(storeId);
      const fallbackDelay = action.fallbackDelayMs ?? 900;

      const fallbackTimer = window.setTimeout(() => {
        if (document.visibilityState === 'visible') {
          window.location.assign(detailUrl);
        }
      }, fallbackDelay);

      window.addEventListener(
        'blur',
        () => {
          window.clearTimeout(fallbackTimer);
        },
        { once: true },
      );

      window.location.assign(reviewUrl);
      return true;
    }
    case 'app-store': {
      const trimmed = action.appId.trim();
      if (!trimmed) {
        return false;
      }
      const id = trimmed.startsWith('id') ? trimmed : `id${trimmed}`;
      window.open(`https://apps.apple.com/app/${id}`, '_blank', 'noopener,noreferrer');
      return true;
    }
    case 'play-store': {
      if (!action.packageName.trim()) {
        return false;
      }
      window.open(
        `https://play.google.com/store/apps/details?id=${encodeURIComponent(action.packageName)}`,
        '_blank',
        'noopener,noreferrer',
      );
      return true;
    }
    case 'url': {
      try {
        const normalized = new URL(action.url).toString();
        openUrl(normalized, action.target ?? '_blank');
        return true;
      } catch {
        return false;
      }
    }
    case 'custom': {
      action.run();
      return true;
    }
    default:
      return false;
  }
}

/**
 * Execute a `FeedbackAction`. Returns `true` if the action was dispatched.
 */
export function runFeedbackAction(action: FeedbackAction): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  switch (action.type) {
    case 'url': {
      try {
        const normalized = new URL(action.url).toString();
        openUrl(normalized, action.target ?? '_blank');
        return true;
      } catch {
        return false;
      }
    }
    case 'mailto': {
      const params = new URLSearchParams();
      if (action.subject) params.set('subject', action.subject);
      if (action.body) params.set('body', action.body);
      const query = params.toString();
      window.location.assign(`mailto:${action.to}${query ? `?${query}` : ''}`);
      return true;
    }
    case 'custom': {
      action.run();
      return true;
    }
    default:
      return false;
  }
}

/**
 * Resolve and run the rate action for the current (or given) platform.
 */
export function rateUs(config: RateUsConfig, platform?: Platform): boolean {
  const { rate } = resolveActions(config, platform);
  if (!rate) {
    return false;
  }
  return runRateAction(rate);
}

/**
 * Resolve and run the feedback action for the current (or given) platform.
 */
export function sendFeedback(config: RateUsConfig, platform?: Platform): boolean {
  const { feedback } = resolveActions(config, platform);
  if (!feedback) {
    return false;
  }
  return runFeedbackAction(feedback);
}
